import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import AvatarGroup from "@mui/material/AvatarGroup";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from '@mui/material/Stack';
import Paper from "@mui/material/Paper";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment/InputAdornment';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

import {
  GridColDef,
  GRID_CHECKBOX_SELECTION_COL_DEF
} from '@mui/x-data-grid';

import useModal from '../../hooks/use-modal';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CustomDataGrid } from '../../components/common/datagrid';
import { articleDetailStyle } from '../../styles/pages/article/article-detail';
import { filterStyle } from '../../styles/common/filter';

import { useQueryArticleDetail, useMutationAddArticleToProject, useMutationDeleteArticle } from '../../api/article';
import { ArticleDetailbyID } from '../../types/article'
import { useQueryProject} from '../../api/project';
import { ProjectList } from '../../types/project';
import { selectAuthUser } from '../../store/auth/selector';

type ArticleContentProps = {
	articleContent: string | undefined
};

function ArticleContent({ articleContent}: ArticleContentProps) {
  // Split the article content into paragraphs
  const paragraphs = articleContent?.split('<p>');

  return (
    <Box>
      {paragraphs?.map((paragraph, idx) => (
        <Typography key={`paragraph-${paragraph}`} variant="body1" mb={2}>
          {paragraph}
        </Typography> 
      ))}
    </Box>
  );
}

export function ArticleDetail() {
  const { showModal } = useModal();
  const navigate = useNavigate();
  const { id } = useParams();
  const profile = useSelector(selectAuthUser);
  const queryClient = useQueryClient();
  
  const [searchResult, setSearchResult] = useState('');
  const [totalItems, setTotalItems] = useState(0)
  const [paginationModel, setPaginationModel] = useState({pageSize: 10, page: 0});
  const [openAddArticleModal, setOpenAddArticleModal] = useState(false);
  const [articleDetail, setArticleDetail] = useState<ArticleDetailbyID | null>(null);
  const [projectList, setProjectList] = useState<ProjectList[]>([]);
  const [doGetProjectList, setDoGetProjectList] = useState(false);
  const [selectedRows, setSelectedRows] = useState<[]>([]);

  const handleClickOpen = () => {
    setDoGetProjectList(true)
    setOpenAddArticleModal(true);
  };

  const handleClose = () => {
    setOpenAddArticleModal(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'projectName',
      headerName: 'Project Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <>
          <Box 
            component="img" 
            src={params.row.projectThumbnail}
            alt={`Image ${params.row.projectThumbnail}`}
            sx={articleDetailStyle.dataGridBox}
          />
          <Link tabIndex={params.tabIndex} onClick={() => navigate('/project-detail')} color="black" underline="hover">
            {params.row.projectName}
          </Link>
        </>
      ),
    },
    {
      field: 'projectOwner',
      headerName: 'Project Owner',
      flex: 0.5,
      minWidth: 100,
    },
    {
      field: 'totalCollaborators',
      headerName: 'Collaborator',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={articleDetailStyle.dataGridBox2}>
          <AvatarGroup spacing="small" max={3} sx={articleDetailStyle.dataGridBox2AvatarGroup}>
            {params.row.collaborators.map((item) => (
              <Avatar key={item.id} alt={item.fullName} src={item.avatar} sx={articleDetailStyle.dataGridBox2AvatarGroupAvatar}/>
            ))}
          </AvatarGroup>
          <Typography variant="caption">{params.row.totalCollaborators} Collaborators</Typography>
        </Box>
      )
    },
    {
      field: 'latestUpdate',
      headerName: 'Latest Update',
      flex: 0.5,
      minWidth: 100,
    },
    {
      width: 100,
      ...GRID_CHECKBOX_SELECTION_COL_DEF
    },
  ];

  const getArticleDetail = useQueryArticleDetail({
    query: {
      articleId: `${id}`,
    },
    options: {
      // enabled: false,
      onSuccess: (res) => {
        setArticleDetail(res.data);
      },
      onError: () => {
        setArticleDetail(null);
      }
    },
  });

  const getProject = useQueryProject({
    query: {
      userId: profile.userId,
      search: searchResult,
      take: paginationModel.pageSize,
      page: paginationModel.page,
      startDate: "",
      endDate: ""
    },
    options: {
      enabled: doGetProjectList,
      onSuccess: (res) => {
        setProjectList(res.data.projects);
        setTotalItems(res.data.totalItems);
      },
      onError: () => {
        setProjectList([]);
      }
    },
  });

  const addArticleToProject = useMutationAddArticleToProject({
    onSuccess: async (res) => {
      showModal({
        title: 'Success',
        message:
          'Add article to project successfully.',
        options: {
          variant: 'success',
          onOk: () => {
            setOpenAddArticleModal(false);
          }
        },
      });
    },
    onError: (error) => {
      showModal({
        title: 'Error',
        message:
          'Add article to project is failed.',
        options: {
          variant: 'failed',
        },
      });
    },
  });

  const addArticleToProjectConfirmation = async () => {
    showModal({
      title: 'Attention',
      message:
        'Are you sure you want to add article to this project?',
      options: {
        variant: 'info',
        buttonTitle: 'Yes',
        buttonTitle2: 'No',
        onOk: () => {
          addArticleToProject.mutate({
            userId: profile.userId,
            articleId: `${id}`,
            projectsId: selectedRows
          });
        },
      },
    });
  }

  const deleteArticle = useMutationDeleteArticle({
    onSettled: async (res) => {
      if (res) {
        queryClient.invalidateQueries({ queryKey: ['article'] });
      }
    },
    onSuccess: async (res) => {
      showModal({
        title: 'Success',
        message:
          'Delete article successfully.',
        options: {
          variant: 'success',
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['article'] });
            navigate('/article-list');
          }
        },
      });
    },
    onError: async (error) => {
      showModal({
        title: 'Error',
        message:
          'Delete article is failed.',
        options: {
          variant: 'failed',
        },
      });
    },
  })

  const deleteProjectConfirmation = (article: string | undefined) => {
    showModal({
      title: 'Attention',
      message:
        'Are you sure you want to delete this item?',
      options: {
        variant: 'info',
        buttonTitle: 'Yes',
        buttonTitle2: 'No',
        onOk: () => {
          deleteArticle.mutate({
            userId: profile.userId,
            articleId: article
          });
        },
      },
    });
  }

  useEffect(() => {
    if(searchResult === null || searchResult === ""){
      getProject.refetch()
    }

  }, [searchResult])

  return (
    <Box sx={articleDetailStyle.contentBox}>
      <Container maxWidth={false}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" onClick={() => navigate('/article-list')}>
                Article
              </Link>
              <Link
                underline="hover"
                color="text.primary"
                onClick={() => navigate('/article-detail')}
                aria-current="page"
              >
                Article Detail
              </Link>
            </Breadcrumbs>
          </Grid>
          <Grid xs={12}>
            <Box sx={articleDetailStyle.contentBoxContainer1GridItemBox}>
              <Typography variant="h3" sx={articleDetailStyle.contentBoxContainer1GridItemBoxTypography}>Article Detail</Typography>
              <Box>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} sx={articleDetailStyle.contentBoxContainer1GridItemBoxBoxButton} onClick={handleClickOpen}>Add article to project</Button>
                <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => deleteProjectConfirmation(articleDetail?.articleId)}>Delete</Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Container maxWidth="md">
        <Paper elevation={0}>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <Typography variant="h3" sx={articleDetailStyle.contentBoxContainer2PaperGridItem1Typography}>{articleDetail?.title}</Typography>
            </Grid>
            <Grid xs={12}>
              <Box sx={articleDetailStyle.contentBoxContainer2PaperGridItem2Box}>
                <Typography>{articleDetail?.publisher}</Typography>
                <Typography color="textSecondary">{articleDetail?.publishedDate}</Typography>
              </Box>
            </Grid>
            <Grid xs={12}>
              <Box
                component="img"
                sx={articleDetailStyle.contentBoxContainer2PaperGridItem3Box}
                alt="article-banner"
                src={articleDetail?.thumbnail}
              />
            </Grid>
            <Grid xs={12}>
              <ArticleContent articleContent={articleDetail?.article} />
            </Grid>
          </Grid>
        </Paper>
      </Container>
      <Dialog
      open={openAddArticleModal}
      onClose={handleClose}
      scroll="paper"
      fullWidth
      maxWidth="lg"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      >
        <DialogTitle id="scroll-dialog-title" sx={articleDetailStyle.dialogDialogTitle}>Project List</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid xs>
                <TextField sx={filterStyle.contentBoxTextField}
                  placeholder="Search Project"
                  variant="outlined"
                  size="small"
                  value={searchResult}
                  onChange={(e) => setSearchResult(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs="auto">
                <Button onClick={() => getProject.refetch()} variant="contained" color="primary">Search / Filter</Button>
              </Grid>
            </Grid>
            <Paper elevation={3}>
              <CustomDataGrid
                columnsData={columns}
                rowsData={projectList}
                rowCount={totalItems}
                getRowId={(row) => row.projectId}
                checkboxSelection
                hideFooterSelectedRowCount
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setSelectedRows(newRowSelectionModel);
                }}
                setPaginationModel={(value) => {
                  setPaginationModel(value);
                }} 
                paginationModel={paginationModel}
              />
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={addArticleToProjectConfirmation}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}